import { KeyboardEvent, MouseEvent, useState } from "react";
import { useSelector } from "react-redux";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import store, { RootState } from "@src/reducers/store";
import { TicketModalInformationForRequestTechnicalSupportModal } from "@src/reducers/your-reducer";
import { CustomerTicket } from "@src/types/support";
import { useI18N } from "@src/language/language-context";
import { useCreateSupportTicketStream } from "@src/cloud-connect/support/hooks/use-create-support-ticket-stream";
import { MAX_TICKET_TITLE_LENGTH } from "@src/cloud-connect/support/constants/constants";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

export const useAddRequestTechnicalSupportDrawerModal = () => {
  const i18n = useI18N();

  const [issueTitle, setIssueTitle] = useState<string>("");
  const [issueDetails, setIssueDetails] = useState<string>("");
  const [images, setImages] = useState<File[]>([]);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(true);

  const { submitSupportTicket, isSubmitLoading } =
    useCreateSupportTicketStream();

  const texts = {
    submitErrorMessage: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.submitErrorMessage",
    ),
    issueTitleTooLongError: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueTitleTooLongError",
    ),
  };

  const ticketModalInformation: TicketModalInformationForRequestTechnicalSupportModal =
    useSelector(
      (state: RootState) =>
        state.yourSlice.modals.addRequestTechnicalSupportDrawerModal
          .ticketModalInformation,
    );

  const userId = store.getState().yourSlice.authContext.userId;
  const isIssueTitleTooLong = issueTitle.length > MAX_TICKET_TITLE_LENGTH;
  const isSSEUnavailable = useSSEUnavailable("service-support");
  const isSubmitDisabled: boolean =
    !issueTitle.trim() ||
    !issueDetails.trim() ||
    isIssueTitleTooLong ||
    isSSEUnavailable;

  const handleToggleDetails = (): void => setIsDetailsExpanded((prev) => !prev);

  const addImages = (files: File[]): void => setImages(files);

  const removeImage = (index: number): void =>
    setImages((prev) => prev.filter((_, i) => i !== index));

  const onChangeIssueTitle = (value: string): void => {
    setIssueTitle(value);
  };

  const onChangeIssueDetails = (value: string): void => {
    setIssueDetails(value);
  };

  const handleSubmitRequestTechnicalSupport = async (
    e: MouseEvent | KeyboardEvent,
  ): Promise<void> => {
    e.preventDefault();

    if (isSSEUnavailable) return;

    if (isIssueTitleTooLong) {
      errorSnackbar(texts.issueTitleTooLongError);
      return;
    }

    const payload: CustomerTicket = {
      type: "CUSTOMER_TICKET",
      raised_by: userId,
      priority: null,
      status: "new",
      title: issueTitle,
      description: issueDetails,
    };

    await submitSupportTicket({
      payload,
      files: images,
      contextQueryDatasource: ticketModalInformation.source_id,
      contextQueryEvents: ticketModalInformation.notification_information
        ? [ticketModalInformation?.notification_information?.event_id]
        : null,
      closeDrawerModalId: "addRequestTechnicalSupportDrawerModal",
      onTicketCreated: () => {
        setIssueTitle("");
        setIssueDetails("");
        setImages([]);
      },
      genericErrorMessage: texts.submitErrorMessage,
    });
  };

  return {
    machineName: ticketModalInformation.source_name,
    issueTitle,
    issueDetails,
    onChangeIssueTitle,
    onChangeIssueDetails,
    handleSubmitRequestTechnicalSupport,
    isSubmitDisabled,
    isSubmitRequestTechnicalSupportLoading: isSubmitLoading,
    isSSEUnavailable,
    isDetailsExpanded,
    handleToggleDetails,
    images,
    addImages,
    removeImage,
  };
};
